import { useEffect, useRef, useState } from "react";

/**
 * Should the current branch selection be dropped?
 *
 * True whenever the parent organisation has changed (including being cleared)
 * while a branch is selected. Pure and exported so the rule is testable and so
 * the hook below cannot quietly diverge from it.
 */
export function shouldClearSelection(
  loadedForOrganisationId,
  organisationId,
  selectedBranchId,
) {
  return (
    loadedForOrganisationId !== organisationId && Boolean(selectedBranchId)
  );
}

/**
 * Keep only branches of the given parent organisation.
 *
 * Defence in depth on top of the server's own scoping: rows without an
 * organisationId are trusted (the selection endpoint omits the field because it
 * is already scoped), but a row claiming a DIFFERENT parent is always dropped.
 */
export function filterBranchesForOrganisation(rows, organisationId) {
  if (!Array.isArray(rows)) return [];
  return rows.filter(
    (b) => !b.organisationId || b.organisationId === organisationId,
  );
}

/**
 * Loads the selectable branches of a parent organisation and enforces the two
 * rules that must never drift between call sites:
 *
 *   1. The list only ever contains branches of `organisationId`.
 *   2. Changing the organisation CLEARS the current branch selection before any
 *      new list arrives -- a branch chosen under organisation A must never
 *      survive a switch to organisation B.
 *
 * Presentation is left to the caller: the join-request card and the work-credential
 * form use different field styling, but both get identical behaviour from here.
 *
 * The server remains the authority -- it re-checks that a submitted branch really
 * belongs to the submitted organisation. This is the usability half of that rule.
 *
 * @param organisationId parent organisation, or null/"" when none is chosen
 * @param fetchBranches  async (organisationId) => branch[] (public or authenticated)
 * @param value          currently selected branch id
 * @param onChange       (branchId) => void, called with "" when cleared
 */
export function useOrganisationBranches(
  organisationId,
  fetchBranches,
  value,
  onChange,
) {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Which organisation the current list belongs to -- lets us tell a real
  // organisation switch from an ordinary re-render.
  const loadedForRef = useRef(null);
  // Keep the latest onChange without making it a dependency, so selecting a
  // branch doesn't re-trigger the fetch.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    let cancelled = false;

    if (loadedForRef.current !== organisationId) {
      // Clear first, synchronously -- never leave a stale foreign branch selected.
      const clear = shouldClearSelection(
        loadedForRef.current,
        organisationId,
        valueRef.current,
      );
      loadedForRef.current = organisationId;
      setBranches([]);
      setError(null);
      if (clear) onChangeRef.current?.("");
    }

    if (!organisationId) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    Promise.resolve(fetchBranches(organisationId))
      .then((list) => {
        if (cancelled) return;
        setBranches(filterBranchesForOrganisation(list, organisationId));
      })
      .catch((err) => {
        if (cancelled) return;
        setBranches([]);
        setError(err?.message ?? "Could not load branches.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [organisationId, fetchBranches]);

  return { branches, loading, error };
}

/** "Chennai Branch - Chennai, Tamil Nadu (CHN001)" */
export function branchLabel(b) {
  const where = [b.city, b.state].filter(Boolean).join(", ");
  const place = where ? ` - ${where}` : "";
  const code = b.branchCode ? ` (${b.branchCode})` : "";
  return `${b.name}${place}${code}`;
}
